<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->
<div class="terms-of-use-container">
	<h1>Syntphony ICT Carbon Insights Terms of Use</h1>

	<section>
		<h2>
			<span class="chapter-number">1</span
			><span class="chapter-title">Introduction and acceptance of Terms</span>
		</h2>
		<p>
			<strong>1.1</strong> These are the terms of use (‘<strong>Terms</strong>’) governing
			access and use of Syntphony ICT Carbon Insights software (hereinafter referred to as the
			“Software Asset”) by individual end users (“<strong>You</strong>”).
		</p>
		<p>
			<strong>1.2</strong> The Software Asset is NTT DATA’s carbon footprint calculator for
			hardware, devices, networks, servers and software.
		</p>
		<p>
			<strong>1.3</strong> These Terms govern your individual use of the Software Asset. The
			contractual relationship between NTT DATA and the organization that has licensed the
			Software Asset is governed by the NTT DATA Global Assets standard Terms and Conditions
			available at
			<a
				href="https://www.syntphony.com/terms-conditions.html"
				target="_blank"
				rel="noopener noreferrer"
				>https://www.syntphony.com/terms-conditions.html</a
			>. In the event of any inconsistency, the NTT DATA Global Assets General Terms and
			Conditions shall prevail.
		</p>
		<p>
			<strong>1.4</strong> Please read these Terms carefully before using the Software Asset.
			By accessing or using the Software Asset you accept and agree to these Terms.
		</p>
		<p>
			<strong>1.5</strong> For purposes of these Terms, the NTT DATA, Inc. Group means NTT
			DATA, Inc and its subsidiaries (<strong>NTT DATA</strong>, ‘us’ and ‘we’).
		</p>
	</section>

	<section>
		<h2>
			<span class="chapter-number">2</span><span class="chapter-title">Changes to Terms</span>
		</h2>
		<p>
			<strong>2.1</strong> We may change these Terms at any time. If we do, we will update the
			‘Version’ and ‘Published Date’ information at the bottom of these Terms. We encourage
			you to regularly review these Terms as your continued access or use of the Software
			Asset may be deemed to be your acceptance of the amended Terms.
		</p>
	</section>

	<section>
		<h2>
			<span class="chapter-number">3</span
			><span class="chapter-title">Permissible use and restrictions</span>
		</h2>
		<p>
			<strong>3.1</strong> The Software Asset is only intended for use by End-Users and
			authorized persons.
		</p>
		<p>
			<strong>3.2</strong> NTT DATA grants you a limited, non-exclusive, non-transferable,
			revocable and non-assignable license to access and use the Software Asset in accordance
			with these Terms.
		</p>
		<p>
			<strong>3.3</strong> You may use the Software Asset and any Content provided on the
			Software Asset only for lawful purposes and in compliance with all applicable laws and
			regulations.
		</p>
		<p>
			<strong>3.4</strong> You agree not to use the Software Asset and/or Content in any way
			that: (a) is unlawful or otherwise breaches any applicable local, national or
			international law or regulation (‘<strong>Applicable Law</strong>’); (b) infringes,
			misappropriates or violates anyone’s rights; (c) is offensive, harmful or inappropriate;
			(d) harvests, collects, or stores data and/or personal data of other users; (e) attempts
			to copy, modify, reproduce, sell, license, trade, resell, or create a derivative work of
			any Content; (f) reverse engineers, decompiles or discovers the software source code of
			the Software Asset, including any algorithms (except to the extent this restriction is
			prohibited by Applicable Law); (g) interferes with or disrupts the software, networks
			cloud services which support the Software Asset; (h) transmits any data, or any material
			that contains viruses, Trojan horses, worms, time-bombs, keystroke loggers, spyware,
			adware or any other harmful programs or similar computer code designed to adversely
			affect the operation of any computer software or hardware; (i) bypasses or breaks any
			protective measures or safety mitigations we put on the Software Asset; (j) uses a
			robot, spider, or other automated means to access the Software Asset; (k) impersonates
			any person or entity or misrepresents your affiliation with a person or entity; or (l)
			imposes an unreasonable or disproportionately large load on our infrastructure.
		</p>
		<p><strong>3.5</strong> You may not:</p>
		<ol type="a">
			<li>
				copy, reproduce, modify, distribute, sell, resell, license, sublicense or create
				derivative works of the Software Asset, except as expressly permitted under these
				Terms;
			</li>
			<li>
				reverse engineer, decompile, disassemble or otherwise attempt to discover the source
				code, object code or underlying algorithms of the Software Asset;
			</li>
			<li>
				interfere with, disrupt or circumvent any security or technical measures implemented
				in the Software Asset;
			</li>
			<li>
				use the Software Asset in any manner that infringes the rights of NTT DATA or any
				third party.
			</li>
		</ol>
	</section>

	<section>
		<h2>
			<span class="chapter-number">4</span
			><span class="chapter-title">Software Asset’s particular conditions</span>
		</h2>
		<p>
			<strong>4.1</strong> You acknowledge that the Software Asset incorporates and relies on
			data from multiple sources, including (i) third-party databases and datasets, (ii)
			partner-provided data, (iii) publicly available data sources, and (iv) datasets
			internally developed by NTT DATA (collectively, the "Third-Party Data Sources"). Such
			data may be subject to limitations, inaccuracies, assumptions, estimation methodologies,
			or periodic updates beyond NTT DATA's control. NTT DATA does not warrant the accuracy,
			completeness, or reliability of any Third-Party Data Sources and shall not be liable for
			any errors or discrepancies originating from such data.
		</p>
		<p>
			<strong>4.2</strong> You shall use any data made available through the Software Asset
			solely within the scope of Your permitted use of the Software. You shall not: (a)
			extract, reproduce, or reuse such data outside the Software; (b) redistribute or
			commercialize such data; or (c) use such data independently from the Software Asset.
		</p>
		<p>
			<strong>4.3</strong> Certain data sources may be subject to third-party rights or
			licensing conditions. You acknowledge that: (i) no ownership rights are transferred to
			You in respect of such data; and (ii) access to such data may be modified, restricted,
			or discontinued where required by the relevant third-party provider. In the event of
			conflict between these Terms and any mandatory third-party condition, the latter shall
			prevail with respect to the relevant data source.
		</p>
	</section>

	<section>
		<h2>
			<span class="chapter-number">5</span
			><span class="chapter-title">Intellectual property</span>
		</h2>
		<p>
			<strong>5.1</strong> NTT DATA is the owner or the licensee of all intellectual property
			rights in the Software Asset and any Content. The Software Asset and Content is
			protected by copyright laws and treaties around the world. NTT DATA retains all rights,
			title, and interest in and to the Software Asset, including all associated intellectual
			property rights.
		</p>
		<p>
			<strong>5.2</strong> Content available on the Software Asset may include the logos,
			trade names, or trademarks of NTT DATA or third parties. Any logos, trade names, or
			trademarks are the property of its respective owner and no person may use them without
			permission.
		</p>
		<p>
			<strong>5.3</strong> Use of the Content or the Software Asset does not transfer any
			intellectual property rights, title, and interest in and to the Content or the Software
			Asset to you.
		</p>
		<p>
			<strong>5.4</strong> Your use of the Software Asset does not transfer any intellectual
			property rights, title, and interest to the Software Asset to you.
		</p>
		<p>
			<strong>5.5</strong> You may not copy, reproduce, republish, upload, post, transmit or
			distribute any Content without written permission of the respective owner. Where
			permitted, you must keep intact all copyright and other proprietary notices.
			Modification of or use of the Content for any other purpose is a violation of the
			copyrights and other proprietary rights of NTT DATA or the respective owners.
		</p>
	</section>

	<section>
		<h2>
			<span class="chapter-number">6</span><span class="chapter-title">Confidentiality</span>
		</h2>
		<p>
			<strong>6.1</strong> Certain Content available on the Software Asset may be meant to be
			kept private or secret, intended for limited access or disclosure only to authorized
			individuals or parties (‘<strong>confidential</strong>’). You may not disclose any
			confidential Content to any person or entity except for someone who: (a) has a
			legitimate need to know the Content; (b) has been advised of the confidential nature of
			the Content; and (c) is bound to NTT DATA by written confidentiality obligations.
		</p>
	</section>

	<section>
		<h2><span class="chapter-number">7</span><span class="chapter-title">Links</span></h2>
		<p>
			<strong>7.1</strong> Where the Software Asset contains links to other websites and
			resources provided by third parties, these links are provided for your information only.
			We are not responsible for anyone else’s website, and those links must not be
			interpreted as approval by us of those linked websites or information you may obtain
			from them. We have no control over the contents of those websites or resources.
		</p>
		<p>
			<strong>7.2</strong> You may link to the Software Asset, provided you do so in a way
			that is fair and legal and does not damage our reputation or take advantage of it. You
			must not establish a link in such a way as to suggest any form of association, approval
			or endorsement on our part where none exists. You must not establish a link to the
			Software Asset on any website that is not owned by you.
		</p>
	</section>

	<section>
		<h2>
			<span class="chapter-number">8</span
			><span class="chapter-title">Breach of these Terms</span>
		</h2>
		<p>
			<strong>8.1</strong> If you breach any of these Terms or infringe any other person’s
			rights, we may, in our sole discretion: (a) block you from using the Software Asset; (b)
			disclose your identity to any third party who is claiming that any content posted or
			uploaded by you to the Software Asset constitutes a violation of their rights; and/or
			(d) take any steps the law allows or we deem appropriate; without affecting any other
			rights we may have at law.
		</p>
		<p>
			<strong>8.2</strong> We exclude our liability for all action we may take in response to
			breaches of these Terms.
		</p>
		<p>
			<strong>8.3</strong> You hereby indemnify, and agree to keep indemnified and defend and
			hold harmless NTT DATA, NTT DATA’s affiliates and our respective officers, directors,
			employees, agents, successors and assigns against any and all claims, proceedings or
			actions, including by data subjects and/or regulatory or supervisory authorities)
			against NTT DATA and direct losses, liabilities, damages, costs (including legal fees on
			a full indemnity basis, regulatory fines, penalties and administrative costs) and
			reasonable expenses incurred by NTT DATA arising out of or in connection with any breach
			of these Terms by you.
		</p>
	</section>

	<section>
		<h2>
			<span class="chapter-number">9</span
			><span class="chapter-title">Limits to our liability</span>
		</h2>
		<p>
			<strong>9.1</strong> The Software Asset is provided ‘as is and as available’. Although
			we make reasonable efforts to update the information, and/or Content on the Software
			Asset, we do not give any express or implied warranty or make any other promise about
			the Software Asset and/or Content obtained through the Software Asset.
		</p>
		<p>
			<strong>9.2</strong> We do not guarantee that the Software Asset will always be
			available or uninterrupted. We may suspend, withdraw, or restrict the availability of
			all or any part of the Software Asset for business or operational reasons, or to comply
			with Applicable Law.
		</p>
		<p>
			<strong>9.3</strong> We will not be liable to you for any loss or damage, whether direct
			or indirect, consequential or punitive, in contract, tort (including negligence), breach
			of statutory duty, or otherwise, even if foreseeable, arising under or in connection
			with use of, or inability to use, the Software Asset or any Content.
		</p>
		<p>
			<strong>9.4</strong> We do not exclude or limit in any way our liability to you where it
			would be unlawful to do so.
		</p>
	</section>

	<section>
		<h2><span class="chapter-number">10</span><span class="chapter-title">Feedback</span></h2>
		<p>
			<strong>10.1</strong> You may choose to, or we may invite you to, submit comments,
			feedback or ideas about our products, services, or the Software Asset
			(<strong>Feedback</strong>). By submitting any Feedback, (a) you agree that Feedback is
			non-confidential and provided voluntarily by you; and (b) we may use such Feedback
			without restriction or compensation to you. You further acknowledge that, by acceptance
			of your submission, we do not waive any rights to use similar or related ideas
			previously known to us, or developed by our employees, or obtained from sources other
			than you.
		</p>
	</section>

	<section>
		<h2><span class="chapter-number">11</span><span class="chapter-title">General</span></h2>
		<p>
			<strong>11.1</strong>
			<span class="paragraph-content"
				><strong>Monitoring and disclosures.</strong> We may monitor your use of the
				Software Asset in accordance with Applicable Law and applicable NTT DATA policies.
				We may disclose any information that we deem necessary to comply with Applicable
				Law, legal process or governmental request.</span
			>
		</p>
		<p>
			<strong>11.2</strong>
			<span class="paragraph-content"
				><strong>Updates.</strong> We may update, enhance, or modify the Software Asset from
				time to time and will provide notice of any modifications, unless such changes are
				of minor nature with no material effect on the features and functionality of the
				Software Asset.</span
			>
		</p>
		<p>
			<strong>11.3</strong>
			<span class="paragraph-content"
				><strong>Suspension and termination.</strong> We may suspend, or terminate the
				Software Asset, or any part thereof, at any time.</span
			>
		</p>
		<p>
			<strong>11.4</strong>
			<span class="paragraph-content"
				><strong>Entire agreement.</strong> The Terms, together with the NTT DATA Global
				Assets standard Terms and Conditions shall constitute the entire agreement between
				the parties with respect to your use of the Software Asset and supersede all prior
				and contemporaneous understandings, agreements, representations and warranties, both
				written and oral, with respect to the use of the Software Asset.</span
			>
		</p>
		<p>
			<strong>11.5</strong>
			<span class="paragraph-content"
				><strong>Waiver.</strong> We never waive (give up) our rights, even if we allow you
				any favor or extension of time, or we delay enforcing our rights against you.</span
			>
		</p>
		<p>
			<strong>11.6</strong>
			<span class="paragraph-content"
				><strong>Severability.</strong> Any Term that is invalid, illegal, or cannot be
				enforced must be regarded as deleted. The remaining Terms continue as
				intended.</span
			>
		</p>
		<p>
			<strong>11.7</strong>
			<span class="paragraph-content"
				><strong>Transfer of rights.</strong> We may transfer our rights and obligations
				under these Terms to another organization. We will always tell you in writing if
				this.</span
			>
		</p>
		<p>
			<strong>11.8</strong>
			<span class="paragraph-content"
				><strong>Export control and sanctions.</strong> You agree to comply with all
				Applicable Laws regarding export control, economic sanctions, customs and
				restrictions on international trade and investments.</span
			>
		</p>
		<p>
			<strong>11.9</strong>
			<span class="paragraph-content"
				><strong>Law and jurisdiction.</strong> These Terms, their subject matter and their
				formation are governed by the law of your company registered office. We both agree
				with the exclusive jurisdiction of the courts of the country of your company
				registered office. We retain the right to bring any suit, action or proceeding
				against you for breach of these Terms in your country of residence or any other
				relevant country.</span
			>
		</p>
		<p>
			<strong>11.10</strong>
			<span class="paragraph-content"
				><strong>Accessibility and language.</strong> These Terms are provided in English.
				If translated into other languages, the English version may prevail in case of
				conflict. We aim to make the Software Asset accessible to users with disabilities
				and welcome feedback to improve accessibility.</span
			>
		</p>
	</section>
</div>
